





































































































































// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import VueJsonPretty from 'vue-json-pretty';
import 'vue-json-pretty/lib/styles.css';

import {
  computed,
  defineComponent, PropType, Ref, ref, watch,
} from 'vue';

import api from '@/api';
import store from '@/store';

// eslint-disable-next-line no-use-before-define
type AnyJson = boolean | number | string | null | JsonArray | JsonMap;
interface JsonMap { [key: string]: AnyJson }
type JsonArray = Array<AnyJson>

const BIND_VAR_PATTERN = /@(@?[a-zA-Z0-9]+)/g;

/**
 * Returns the first index at which these arrays differ.
 * Assumes the length of the arrays differ by exactly one.
 */
function findDifferenceIndex<T = unknown>(arr1: T[], arr2: T[]) {
  if (arr1.length === arr2.length) {
    throw new Error('Arrays have the same length!');
  }

  let smaller: T[];
  let larger: T[];
  if (arr1.length < arr2.length) {
    smaller = arr1;
    larger = arr2;
  } else {
    smaller = arr2;
    larger = arr1;
  }

  // Iterate, return first differing
  let i = 0;
  while (i < smaller.length) {
    if (smaller[i] !== larger[i]) {
      break;
    }
    i += 1;
  }

  return i;
}

export default defineComponent({
  name: 'AQLWizard',
  components: {
    VueJsonPretty,
  },
  props: {
    workspace: {
      type: String as PropType<string>,
      required: true,
    },
  },
  setup(props) {
    const lastQueryResults: Ref<null | Array<JsonArray>> = ref(null);
    const loading = ref(false);
    const queryErrorMessage = ref('');
    const createTableMenu = ref(false);
    const createTableErrorMessage: Ref<null | string> = ref(null);
    const createTableName: Ref<null | string> = ref(null);

    const query = ref('FOR doc IN @@TABLE LIMIT 10 RETURN doc');
    const default_query_finished = ref(false);
    watch(query, () => {
      if (queryErrorMessage.value) { queryErrorMessage.value = ''; }
    });

    const bind_var_values = ref([] as string[]);
    const bind_var_keys = computed(() => {
      const matches = [...query.value.matchAll(BIND_VAR_PATTERN)] as [string, string][];

      // Convert to obj then array to make unique
      return Object.keys(
        matches.reduce((obj, key) => ({ ...obj, [key[1]]: '' }), {}),
      );
    });
    watch(bind_var_keys, (val: string[], oldVal: string[]) => {
      if (val.length !== oldVal.length) {
        // Find differing index
        const index = findDifferenceIndex(val, oldVal);

        // New key was added
        if (val.length > oldVal.length) {
          bind_var_values.value.splice(index, 0, '');
        } else { // Key was removed
          bind_var_values.value.splice(index, 1);
        }
      }
    });

    const nodeTables = computed(() => store.getters.nodeTables.map((table) => table.name));
    watch(nodeTables, () => {
      if (nodeTables.value.length && !default_query_finished.value) {
        // eslint-disable-next-line prefer-destructuring
        bind_var_values.value[0] = nodeTables.value[0];
        default_query_finished.value = true;
      }
    });

    const edgeTables = computed(() => store.getters.edgeTables.map((table) => table.name));
    const networks = computed(() => store.getters.networks.map((network) => network.name));
    const workspaceInfo = computed(() => [
      { title: 'Node Tables', data: nodeTables.value },
      { title: 'Edge Tables', data: edgeTables.value },
      { title: 'Networks', data: networks.value, network: true },
    ]);

    function detailLink(name: string, network: boolean) {
      const routeName = network ? 'networkDetail' : 'tableDetail';

      return { name: routeName, params: { workspace: props.workspace, table: name, network: name } };
    }

    async function runQuery() {
      if (!query.value) {
        queryErrorMessage.value = 'Query cannot be empty.';
        return;
      }

      loading.value = true;

      // Create bind vars object
      const bind_vars = (bind_var_keys.value).reduce((obj, key, index) => ({ ...obj, [key]: bind_var_values.value[index] }), {});

      try {
        const resp = await api.aql(props.workspace, { query: query.value, bind_vars });
        lastQueryResults.value = resp;
        queryErrorMessage.value = '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        queryErrorMessage.value = error.response.data;
        lastQueryResults.value = null;
      }

      loading.value = false;
    }

    store.dispatch.fetchWorkspace(props.workspace);

    return {
      query,
      bind_var_keys,
      bind_var_values,
      lastQueryResults,
      loading,
      queryErrorMessage,
      createTableMenu,
      createTableErrorMessage,
      createTableName,
      workspaceInfo,
      detailLink,
      runQuery,
    };
  },
});
